/**
 * Repository Layer Composition
 * Combines all services into a unified layer for the offline repository
 */

import * as Layer from 'effect/Layer';
import { AppLayer } from '@/api/client';

// Import all services
import { LoggerLive, DefaultLoggerConfigLayer } from './logger/logger.service';
import { ClockLive } from './clock/clock.service';
import { MetricsLive } from './metrics/metrics.service';
import { StorageServiceLive, DefaultStorageConfigLayer } from './storage/storage.service';
import { RepositoryEventsLive } from './events/repository-events';
import { ConflictResolverLive } from './conflict/conflict-resolver.service';
import { SyncServiceLive } from './sync/sync.service';

// Base services layer (no dependencies)
const BaseServicesLayer = Layer.mergeAll(
  LoggerLive.pipe(Layer.provide(DefaultLoggerConfigLayer)),
  ClockLive,
  MetricsLive
);

// Event system (no dependencies)
const EventsLayer = RepositoryEventsLive;

// Storage layer (depends on config and RepositoryEvents for auto-emitted
// mutation events). EventsLayer here is the same layer instance merged into
// RepositoryServicesLayer below, so Effect layer memoization guarantees that
// storage and event subscribers share a single PubSub.
const StorageLayer = Layer.provide(
  StorageServiceLive,
  Layer.mergeAll(DefaultStorageConfigLayer, BaseServicesLayer, EventsLayer)
);

// Conflict resolver (depends on Logger and Clock)
const ConflictLayer = Layer.provide(ConflictResolverLive, BaseServicesLayer);

// Sync service (depends on Storage, Logger, Clock, Metrics, Events, Conflicts, ApiClient)
const SyncLayer = Layer.provide(
  SyncServiceLive,
  Layer.mergeAll(BaseServicesLayer, StorageLayer, EventsLayer, ConflictLayer, AppLayer)
);

// Repository layer combines all services
export const RepositoryServicesLayer = Layer.mergeAll(
  BaseServicesLayer,
  StorageLayer,
  EventsLayer,
  ConflictLayer,
  SyncLayer,
  AppLayer
);

// Export individual layers for testing
export {
  LoggerLive,
  ClockLive,
  MetricsLive,
  StorageServiceLive,
  RepositoryEventsLive,
  ConflictResolverLive,
  SyncServiceLive,
  BaseServicesLayer,
};
